import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Laptop, ShieldAlert } from "lucide-react";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import { StaleSessionNotice } from "@/features/account/components/StaleSessionNotice";
import { formatDate } from "@/lib/formatters";
import { ApiError, isStaleSessionError } from "@/lib/api/client";
import {
  MFA_SETUP_REQUIRED_CODE,
  REAUTH_REQUIRED_CODE,
  type CliAuthorizationMetadata,
} from "../api/cli.api";
import { useApproveCliAuthorization, useDenyCliAuthorization } from "../hooks/useCli";
import { CodesAtRiskReminder } from "./CodesAtRiskReminder";

type Outcome = "pending" | "approved" | "denied" | "leaving";

interface CliAuthorizePanelProps {
  authorization: CliAuthorizationMetadata | undefined;
  isLoading: boolean;
  error: ApiError | null;
  onReauthenticate: () => void;
}

function focusHeadingTransiently(heading: HTMLElement): boolean {
  if (heading.hasAttribute("tabindex")) {
    heading.focus();
    return document.activeElement === heading;
  }
  heading.tabIndex = -1;
  heading.focus();
  const blurOnClick = () => {
    if (document.activeElement === heading) heading.blur();
  };
  const release = () => {
    heading.removeEventListener("blur", release);
    heading.removeEventListener("pointerdown", release, true);
    heading.removeEventListener("click", blurOnClick, true);
    heading.removeAttribute("tabindex");
  };
  if (document.activeElement !== heading) {
    release();
    return false;
  }
  heading.addEventListener("blur", release);
  heading.addEventListener("pointerdown", release, true);
  heading.addEventListener("click", blurOnClick, true);
  return true;
}

export function CliAuthorizePanel({
  authorization,
  isLoading,
  error,
  onReauthenticate,
}: CliAuthorizePanelProps) {
  const { t } = useTranslation();
  const approve = useApproveCliAuthorization();
  const deny = useDenyCliAuthorization();
  const [outcome, setOutcome] = useState<Outcome>("pending");
  const headingRef = useRef<HTMLHeadingElement>(null);
  const focusedRequestHeading = useRef(false);

  const goneAfterAnswer = approve.error?.status === 404 || deny.error?.status === 404;
  const gone = error?.status === 404 || goneAfterAnswer;
  const loadStale = error?.status === 401 || isStaleSessionError(error);
  const showRequestCard =
    outcome === "pending" && !isLoading && !gone && !loadStale && !error && authorization != null;

  useLayoutEffect(() => {
    if (!showRequestCard || focusedRequestHeading.current) return;
    const heading = headingRef.current;
    if (heading == null) return;
    focusedRequestHeading.current = true;
    focusHeadingTransiently(heading);
  }, [showRequestCard]);

  if (outcome === "approved") {
    return (
      <Card announce>
        <h1 className="text-lg font-semibold text-heading">{t("cli.authorize.approvedTitle")}</h1>
        <p className="mt-2 text-sm text-muted">{t("cli.authorize.approvedBody")}</p>
      </Card>
    );
  }

  if (outcome === "denied") {
    return (
      <Card announce>
        <h1 className="text-lg font-semibold text-heading">{t("cli.authorize.deniedTitle")}</h1>
        <p className="mt-2 text-sm text-muted">{t("cli.authorize.deniedBody")}</p>
      </Card>
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  if (gone) {
    return (
      <Card announce={goneAfterAnswer}>
        <h1 className="text-lg font-semibold text-heading">{t("cli.authorize.expiredTitle")}</h1>
        <p className="mt-2 text-sm text-muted">{t("cli.authorize.expiredBody")}</p>
      </Card>
    );
  }

  if (loadStale) {
    return (
      <Card>
        <h1 className="text-lg font-semibold text-heading">{t("cli.authorize.title")}</h1>
        <StaleSessionNotice
          returnTo="/"
          className="mt-4"
          body={
            isStaleSessionError(error)
              ? t("cli.authorize.staleSessionBody")
              : t("cli.authorize.reauthBody")
          }
          fullWidth
          autoFocus
          onSignInAgain={onReauthenticate}
        />
      </Card>
    );
  }

  if (error || !authorization) {
    return (
      <Card>
        <ApiErrorBanner error={error} />
      </Card>
    );
  }

  const busy = approve.isPending || deny.isPending || outcome === "leaving";
  const approving = approve.isPending || approve.isSuccess;
  const latestAnswer = deny.submittedAt > approve.submittedAt ? deny : approve;
  const approvalError = latestAnswer.error;
  const staleSession = isStaleSessionError(approve.error) || isStaleSessionError(deny.error);
  const mfaSetupRefused =
    approve.error?.status === 403 && approve.error.code === MFA_SETUP_REQUIRED_CODE;
  const needsMfaSetup =
    !staleSession && (authorization.mfaSetupOverdue === true || mfaSetupRefused);
  const needsStepUp =
    !needsMfaSetup &&
    !staleSession &&
    (authorization.stepUpRequired ||
      (approve.error?.status === 403 && approve.error.code === REAUTH_REQUIRED_CODE));

  const handleApprove = () => {
    approve.mutate(authorization.authorizationId, {
      onSuccess: (result) => {
        if (result.redirectUri) {
          setOutcome("leaving");
          window.location.assign(result.redirectUri);
          return;
        }
        setOutcome("approved");
      },
    });
  };

  const handleDeny = () => {
    deny.mutate(authorization.authorizationId, {
      onSuccess: (result) => {
        if (result.redirectUri) {
          setOutcome("leaving");
          window.location.assign(result.redirectUri);
          return;
        }
        setOutcome("denied");
      },
    });
  };

  return (
    <Card>
      <div className="flex items-center gap-2">
        <Laptop size={18} className="text-muted" />
        <h1 ref={headingRef} className="text-lg font-semibold text-heading">
          {t("cli.authorize.title")}
        </h1>
      </div>
      <p className="mt-2 text-sm text-muted">{t("cli.authorize.subtitle")}</p>

      <dl className="mt-6 space-y-3 text-sm">
        <Detail
          label={t("cli.authorize.device")}
          value={authorization.deviceName || t("cli.authorize.unknownDevice")}
        />
        <Detail label={t("cli.authorize.client")} value={clientLabel(authorization)} />
        {authorization.clientPlatform ? (
          <Detail label={t("cli.authorize.platform")} value={authorization.clientPlatform} />
        ) : null}
        {authorization.requestIp ? (
          <Detail label={t("cli.authorize.origin")} value={authorization.requestIp} />
        ) : null}
        <Detail
          label={t("cli.authorize.requestExpires")}
          value={formatDate(authorization.expiresAt, { hour: "2-digit", minute: "2-digit" })}
        />
      </dl>

      <p className="mt-4 text-sm text-heading">{t("cli.authorize.grant")}</p>

      <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-elevated p-3 text-xs text-muted">
        <ShieldAlert size={16} className="mt-0.5 shrink-0 text-danger" />
        <p>{t("cli.authorize.warning")}</p>
      </div>

      {approvalError &&
      approvalError.code !== MFA_SETUP_REQUIRED_CODE &&
      !isStaleSessionError(approvalError) ? (
        <ApiErrorBanner error={approvalError} className="mt-4" />
      ) : null}

      {needsMfaSetup ? (
        <div className="mt-6">
          <p
            key={mfaSetupRefused ? "mfa-setup-refused" : "mfa-setup-sentence"}
            role={mfaSetupRefused ? "alert" : undefined}
            className="text-sm text-muted"
          >
            {t("cli.authorize.mfaSetupBody")}
          </p>
          <a
            href="/account"
            className="mt-3 block w-full rounded-lg bg-indigo px-4 py-2.5 text-center text-sm font-medium text-white transition-colors hover:bg-indigo-bright"
          >
            {t("cli.authorize.mfaSetupAction")}
          </a>
          <button
            type="button"
            onClick={handleDeny}
            disabled={busy}
            className="mt-3 w-full rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("cli.authorize.deny")}
          </button>
        </div>
      ) : staleSession ? (
        <StaleSessionNotice
          key="cli-stale-session-notice"
          returnTo="/"
          className="mt-6"
          body={t("cli.authorize.staleSessionBody")}
          fullWidth
          autoFocus
          onSignInAgain={onReauthenticate}
        />
      ) : needsStepUp ? (
        <div className="mt-6">
          <p className="text-sm text-muted">{t("cli.authorize.reauthBody")}</p>
          <button
            type="button"
            onClick={onReauthenticate}
            className="mt-3 w-full rounded-lg bg-indigo px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-bright"
          >
            {t("cli.authorize.reauthAction")}
          </button>
          <button
            type="button"
            onClick={handleDeny}
            disabled={busy}
            className="mt-3 w-full rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("cli.authorize.deny")}
          </button>
        </div>
      ) : (
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={handleApprove}
            disabled={busy}
            className="flex-1 rounded-lg bg-indigo px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-bright disabled:cursor-not-allowed disabled:opacity-60"
          >
            {approving ? t("cli.authorize.approving") : t("cli.authorize.approve")}
          </button>
          <button
            type="button"
            onClick={handleDeny}
            disabled={busy}
            className="flex-1 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
          >
            {t("cli.authorize.deny")}
          </button>
        </div>
      )}
    </Card>
  );
}

function clientLabel(authorization: CliAuthorizationMetadata): string {
  const name = authorization.clientName || "aether-cli";
  return authorization.clientVersion ? `${name} ${authorization.clientVersion}` : name;
}

function Card({ children, announce = false }: { children: React.ReactNode; announce?: boolean }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-6">
      {announce ? (
        <div role="alert">
          {children}
          <CodesAtRiskReminder />
        </div>
      ) : (
        <>
          {children}
          <CodesAtRiskReminder />
        </>
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-right font-medium text-heading">{value}</dd>
    </div>
  );
}
